import React from 'react';

import { Interval, DateTime, Duration } from 'luxon';

import MapWidget from './MapWidget.js';
import ControlWidget from './ControlWidget.js'
import TornadoCollection from './TorCollection.js';
import {f_scale_partitioner} from './Partitioner.js';
import EnvironmentDataHandler from './environment/EnvironmentDataHandler.js';
import {SelectionInterval} from './SelectionInterval.js';
import MapOverlayWidget from './MapOverlayWidget.js';
import { DomainFilter, Filter, PartitionFilter } from './Filter.js';
import Animator from './Animator.js';

import './App.css';
import MultiscaleTimeAxis from './MultiscaleTimeAxis.js';


const DATA_LIMITS = Interval.fromDateTimes(DateTime.utc(1700, 1, 1, 0), DateTime.utc(2022, 1, 1, 0));
const USE_DEV_SERVER = process.env.NODE_ENV == 'development' || process.env.REACT_APP_BUILD == 'development' 
                       || process.env.REACT_APP_BUILD == 'beta';

const DOMAINS = {
    'World': {
        'fit_bounds': [[-180, -75], [180, 75]],
        'filter': new DomainFilter(),
        'cday_offset': Duration.fromObject({'hours': 0}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['ESWD', 'NIWA', 'Sources'],
        'env_name': 'world',
        'small_datafile_intvs': [],
    },
    'North America': {
        'fit_bounds': [[-169, 12], [-51, 72]],
        'filter': new DomainFilter(['us', 'ca', 'mx', 'bm']),
        'cday_offset': Duration.fromObject({'hours': 12}),
        'units': 'imperial',
        'has_states': true,
        'attribution': ['Sources'],
        'env_name': 'na',
        'small_datafile_intvs': [[null, 1899], [1900, 1924], [1925, 1949]].concat(Array.from({'length': 72}, (yr, iyr) => [1950 + iyr, 1950 + iyr])),
    },
    'South America': {
        'fit_bounds': [[-83, -57], [-32, 15]],
        'filter': new DomainFilter(['ar', 'uy', 'cl', 'br', 'py']),
        'cday_offset': Duration.fromObject({'hours': 12}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['Sources'],
        'env_name': 'sa',
        'small_datafile_intvs': [[null, null]],
    },
    'Europe': {
        'fit_bounds': [[-25, 28], [41, 70]],
        'filter': new DomainFilter(['eu', 'al', 'at', 'by', 'be', 'ba', 'bg', 'hr', 'cy', 'cz', 'dk', 'ee', 'fi', 'fr', 'ge',
                                    'de', 'gr', 'hu', 'is', 'ie', 'it', 'xk', 'lv', 'lt', 'lu', 'mk', 'mt', 'md', 'me', 'nl',
                                    'no', 'pl', 'pt', 'ro', 'ru', 'rs', 'sk', 'si', 'es', 'se', 'ch', 'ua', 'gb', 'tn', 'ma',
                                    'dz', 'ly']),
        'cday_offset': Duration.fromObject({'hours': 6}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['ESWD', 'Sources'],
        'env_name': 'eu',
        'small_datafile_intvs': [[null, 1899], [1900, 1939], [1940, 1979], [1980, 1989], [1990, 1999]].concat(
            Array.from({'length': 22}, (yr, iyr) => [2000 + iyr, 2000 + iyr])
        ),
    },
    'Western Asia': {
        'fit_bounds': [[34, 30], [90, 60]],
        'filter': new DomainFilter(['ru', 'tr', 'kg', 'tm', 'kz', 'ge', 'il', 'sy', 'az', 'am', 'lb', 'jo']),
        'cday_offset': Duration.fromObject({'hours': 0}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['ESWD', 'Sources'],
        'env_name': 'was',
        'small_datafile_intvs': [[null, 1999], [2000, 2009], [2010, 2015], [2016, 2021]],
    },
    'Eastern Asia': {
        'fit_bounds': [[67, -10], [190, 60]],
        'filter': new DomainFilter(['ru', 'jp', 'cn', 'id', 'in', 'bd']),
        'cday_offset': Duration.fromObject({'hours': 0}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['ESWD', 'Sources'],
        'env_name': 'eas',
        'small_datafile_intvs': [[null, 1999], [2000, 2009], [2010, 2014], [2015, 2021]],
    },
    'Southern Africa': {
        'fit_bounds': [[11, -35], [52, -8]],
        'filter': new DomainFilter(['za', 'mg']),
        'cday_offset': Duration.fromObject({'hours': 6}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['Sources'],
        'env_name': 'saf',
        'small_datafile_intvs': [[null, null]],  
    },
    'Oceania': {
        'fit_bounds': [[112, -48], [181, -10]],
        'filter': new DomainFilter(['au', 'nz']),
        'cday_offset': Duration.fromObject({'hours': 0}),
        'units': 'metric',
        'has_states': false,
        'attribution': ['NIWA', 'Sources'],
        'env_name': 'oc',
        'small_datafile_intvs': [[null, null]],
    }
};
const DOMAIN_TITLES = Object.keys(DOMAINS);

const ENV_INTV_DUR_CUTOFF = Duration.fromISO('PT72H');
const ENV_DATA_BASE = USE_DEV_SERVER ? '/dev/env' : '/env';

const TIMELINE_MARGINS = {'top': 5, 'bottom': 30, 'left': 40, 'right': 55};

class TornadoArchiveApp extends React.Component {
    constructor(props) {
        super(props);

        this.reanalysis_src = 'era5';

        this.base_tor_collection = null;
        this.domain_tor_collection = null;
        this.partitioner = f_scale_partitioner;

        const default_domain = this.props['default_domain'];
        const cday_offset = DOMAINS[default_domain]['cday_offset']

        this.default_cday = this.props['default_day'];
        const intv = new SelectionInterval(Interval.after(this.default_cday.plus(cday_offset), {'hours': 24}));

        this.env_handler = new EnvironmentDataHandler(ENV_DATA_BASE);
        this.env_data_dintv = Duration.fromISO('PT1H');
        this.animator = new Animator(500, this.advanceTime.bind(this), dt => {
            this.setState({'sel_data_time': dt});
            this._updateURLFragment({'sel_data_time': dt});
        });

        this.anim_timer = null;
        this.anim_delay = 500; // milliseconds

        const frag_state = this._parseURLFragment();
        this.initial_window_width = window.innerWidth;
        this.initial_window_height = window.innerHeight;
        if (window.innerWidth > window.innerHeight) {
            this.initial_window_width = window.innerHeight;
            this.initial_window_height = window.innerWidth;
        }

        const default_state = {
            'controls_visible': true,

            'interval': intv,

            'filters': {
                'partition': new PartitionFilter(this.partitioner)
            },

            'sel_data_time': null,
            'env_data_times': [],
            'env_type': 'td2m',

            'map': null,

            'tor_collection': null,

            'is_animating': false,
            'temp_display': 'All',

            'domain': default_domain,

            'window_size': {'width': window.innerWidth, 'height': window.innerHeight},
        };

        this.state = {...default_state, ...frag_state};

        const dom_data_limits = Interval.fromDateTimes(DATA_LIMITS.start.plus(cday_offset), DATA_LIMITS.end.plus(cday_offset));
        this.state['timeline_taxis'] = new MultiscaleTimeAxis(this.state['interval'].getPaddedInterval(), 
            [TIMELINE_MARGINS['left'], this.state['window_size']['width'] - TIMELINE_MARGINS['right']], dom_data_limits, 15);

        // This will break things when the interval start is not during a day with some initial data
        this.default_cday = this.state['interval'].start();

        this.ss_funcs = {};
        this.set_screenshot_func = this.set_screenshot_func.bind(this);
        this.stale_url_fragment = false;

        this.keypress = this.keypress.bind(this);
        this.resize = this.resize.bind(this);

        this.random_day_func = this.env_handler.getRandomDate.bind(this.env_handler);
        this.onshowhidecontrols = this.onshowhidecontrols.bind(this);
        this.onintervalchange = this.onintervalchange.bind(this);
        this.onfilterchange = this.onfilterchange.bind(this);
        this.ontboundschange = this.ontboundschange.bind(this);
        this.onenvlayerchange = this.onenvlayerchange.bind(this);
        this.onenvtimechange = this.onenvtimechange.bind(this);
        this.onanimtoggle = this.onanimtoggle.bind(this);
        this.onanimspeedchange = this.onanimspeedchange.bind(this);
        this.onscreenshot = this.onscreenshot.bind(this);
        this.ondomainchange = this.ondomainchange.bind(this);
        this.ontempdisplaychange = this.ontempdisplaychange.bind(this);
    }

    async componentDidMount() {
        window.addEventListener('keydown', this.keypress);
        window.addEventListener('resize', this.resize);
        this._updateURLFragment();

        this.env_handler.getAvailableDates(DOMAINS[this.state['domain']]['env_name'], this.reanalysis_src, 
            () => this.checkEnvDataTimes(this.state['interval'], true));

        const url_base = USE_DEV_SERVER ? '/dev/data' : '/data';

        const now = DateTime.utc();

        const downloadFile = async url => {
            let tor_coll = await TornadoCollection.fromServer(url + '?' + now.toISO());

            if (this.base_tor_collection !== null) {
                this.base_tor_collection.deleteInstance();
            }
            this.base_tor_collection = tor_coll;

            const new_domain_tor_collection = await tor_coll.applyFilters({'domain': DOMAINS[this.state['domain']]['filter']});
            if (this.domain_tor_collection !== null) {
                this.domain_tor_collection.deleteInstance();
            }
            this.domain_tor_collection = new_domain_tor_collection;

            const new_tor_collection = await this.domain_tor_collection.applyFilters(this.state['filters']);
            if (this.state['tor_collection'] !== null) {
                this.state['tor_collection'].deleteInstance();
            }
            this.setState({'tor_collection': new_tor_collection});
            this.onintervalchange(this.state['interval'], false);
        }

        const sd_intvs = DOMAINS[this.state['domain']]['small_datafile_intvs'].filter(intv => {
            const [ylb, yub] = intv;
            if (ylb === null && yub === null) {
                return true;
            }
            else if (ylb === null) {
                return this.default_cday.year <= yub;
            }
            return ylb <= this.default_cday.year && this.default_cday.year <= yub;
        });

        let yr_str;
        if (sd_intvs.length > 0) {
            const [sd_ylb, sd_yub] = sd_intvs[0];
            if (sd_ylb === null && sd_yub === null) {
                yr_str = '';
            }
            else if (sd_ylb === null) {
                yr_str = '_None-' + sd_yub;
            }
            else if (sd_ylb == sd_yub) {
                yr_str = '_' + sd_ylb;
            }
            else {
                yr_str = '_' + sd_ylb + '-' + sd_yub;
            }
        }
        else {
            yr_str = '';
        }

        try {
            await downloadFile(url_base + '/' + DOMAINS[this.state['domain']]['env_name'] 
                               + '/tornadoes' + yr_str + '.json.gz');
        }
        catch (e) {
            console.warn("Couldn't download yearly subset");
        }

        await downloadFile(url_base + '/tornadoes.json.gz');
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.keypress);
        window.removeEventListener('resize', this.resize);
    }

    onintervalchange(intv, recenter_timeline) {
        let reset_sel_data_time;
        let new_state;
        if (recenter_timeline) {
            const intv_pad = intv.getPaddedInterval();
            new_state = {'interval': intv, 'timeline_taxis': this.state['timeline_taxis'].withInterval(intv_pad), 'sel_data_time': null};
            reset_sel_data_time = true;
        }
        else {
            new_state = {'interval': intv};
            reset_sel_data_time = (this.state['sel_data_time'] === null);
        }

        if (intv.duration() >= ENV_INTV_DUR_CUTOFF && this.state['temp_display'] == 'Hourly') {
            new_state['temp_display'] = 'All';
        }

        this.setState(new_state);

        this._updateURLFragment({'interval': intv});
        this.checkEnvDataTimes(intv, reset_sel_data_time);
    }

    ontboundschange(tbnds) {
        const tbnds_intv = new SelectionInterval(tbnds);
        this.setState({'timeline_taxis': this.state['timeline_taxis'].withInterval(tbnds_intv)});
    }

    async onfilterchange(new_filters) {
        new_filters = {...this.state['filters'], ...new_filters};
        new_filters = Object.fromEntries(Object.entries(new_filters).filter(([k, v]) => v !== null));

        let new_state = {'filters': new_filters}

        if (this.domain_tor_collection !== null) {
            new_state['tor_collection'] = await this.domain_tor_collection.applyFilters(new_filters);
        }

        if (this.state['tor_collection'] !== null) {
            this.state['tor_collection'].deleteInstance();
        }
        this.setState(new_state);
        this._updateURLFragment({'filters': new_filters});
    }

    onscreenshot() {
        let win = window.open();
        if (win !== null) {
            const header = `<head>
            <title>Tornado Archive Screenshot [Rendering ...]</title>
            <style>
            h1 { width: 100%; text-align: center; position: fixed; top: 50%; transform: translate(0, -50%); }
            </style>
            </head>`;
            const body = "<body><h1>Rendering Screenshot ...</h1></body>";

            win.document.open();
            win.document.write(header + body);
            win.document.location = '#';
            win.document.close();

            this.screenshotCurrentFrame().then(data => {
                const header = "<head><title>Tornado Archive Screenshot</title></head>";
                const iframe = "<body><img width='100%' src='" + String(data) + "'></body>";

                win.document.open();
                win.document.write(header + iframe);
                win.document.location = '#';
                win.document.close();
            });
        }
    }

    screenshotCurrentFrame() {
        // Poll all the child components for screenshots
        const promise_map = this.ss_funcs['map']().then(data_map => {
            const prom_map = new Promise((resolve, reject) => {
                let img = new Image();
                img.onload = () => resolve(img);

                img.src = data_map;
            })
            return prom_map;
        })

        const promise_overlay = this.ss_funcs['overlay']().then(data_overlay => {
            const prom_overlay = new Promise((resolve, reject) => {

                let img = new Image();
                img.onload = () => resolve(img);

                img.src = data_overlay;
            })

            return prom_overlay;
        });

        const promise_timeline = new Promise((resolve, reject) => {
            const data = this.ss_funcs['timeline']();

            let img = new Image();
            img.onload = () => resolve(img);

            img.src = data;
        })

        // Composite them and open a new window
        return Promise.allSettled([promise_map, promise_overlay, promise_timeline]).then(results => {
            const map_img = results[0]['value'];
            const overlay_img = results[1]['value'];
            const timeline_img = results[2]['value'];

            const full_width = map_img.width;
            const full_height = map_img.height + timeline_img.height + 4;

            let canvas = document.createElement('canvas');
            canvas.width = full_width;
            canvas.height = full_height;

            let ctx = canvas.getContext('2d');
            ctx.beginPath();
            ctx.fillStyle = '#ffffff';
            ctx.rect(0, 0, full_width, full_height);
            ctx.fill();

            ctx.drawImage(map_img, 0, 0);
            ctx.drawImage(overlay_img, 0, 0);
            ctx.drawImage(timeline_img, 0, map_img.height + 4);

            return canvas.toDataURL();
        });
    }

    set_screenshot_func(ss_func, which) {
        this.ss_funcs[which] = ss_func;
    }

    _updateURLFragment(data) {
        if (data === undefined) {
            data = {};
        }

        let keys = {
            'interval': i => i.toURLFragment(),
            'map': m => m['center_lon'].toFixed(4) + ';' + m['center_lat'].toFixed(4) + ';' + m['zoom'].toFixed(2),
            'env_type': t => t,
            'sel_data_time': t => t.toISO(),
            'domain': d => d,
            'filters': f => Object.entries(f).map(([fn, f_]) => fn + '|' + f_.toRepr()).join(';'),
        };

        let url_frags = Object.fromEntries(Object.keys(keys).map(k => [k, this.state[k]]));
        url_frags = {...url_frags, ...data};

        if (this.state['sel_data_time'] === null || url_frags['sel_data_time'] === null) {
            delete url_frags['sel_data_time'];
        }

        if (this.state['map'] === null || url_frags['map'] === null) {
            delete url_frags['map'];
        }
        
        const frag_str = Object.entries(url_frags).map(([k, v]) =>  k + '=' + keys[k](v)).join('&');

        const window_obj = window.self !== window.top ? window.parent : window;
        const url_no_hash = window_obj.location.href.split('#')[0];
        window_obj.history.replaceState(null, '', url_no_hash + '#' + frag_str);
    }

    _parseURLFragment() {
        const map_keys = ['center_lon', 'center_lat', 'zoom'];

        const keys = {
            'interval': (i, o) => {
                const dom_data_limits = Interval.fromDateTimes(DATA_LIMITS.start.plus(o), DATA_LIMITS.end.plus(o));
                return SelectionInterval.fromURLFragment(i, o, dom_data_limits);
            },
            'map': m => Object.fromEntries(m.split(';').map((mv, imv) => [map_keys[imv], parseFloat(mv)])),
            'env_type': t => t == 'null' ? null : t,
            'sel_data_time': t => DateTime.fromISO(t, {'zone': 'utc'}),
            'domain': decodeURI,
            'filters': f => Object.fromEntries(decodeURI(f).split(';').map(f_ => {
                const filter_bits = f_.split('|');
                return [filter_bits[0], Filter.fromRepr(filter_bits.slice(1).join('|'))];
            })),
        };

        const parse_entry = (entry, other) => {
            const bits = entry.split('=');
            const item_name = bits[0];
            const item_str = bits.slice(1).join("=");

            const item = keys[item_name](item_str, other);
            return [item_name, item];
        }

        const window_obj = window.self !== window.top ? window.parent : window;
        const entry_strs = window_obj.location.hash.slice(1).split('&');
        let url_frags = Object.fromEntries(entry_strs.filter(entry => entry != '')
                                                     .filter(entry => !entry.startsWith('interval')).map(parse_entry));

        const intv_entry = entry_strs.filter(entry => entry.startsWith('interval'))
                                     .map(entry => parse_entry(entry, DOMAINS[url_frags['domain']]['cday_offset']))[0];

        if (intv_entry !== undefined) {
            url_frags['interval'] = intv_entry[1];
        }

        return url_frags;
    }
    
    onenvtimechange(new_time) {
        this.setState({'sel_data_time': new_time});
        this._updateURLFragment({'sel_data_time': new_time});
    }

    onenvlayerchange(new_type) {
        this.setState({'env_type': new_type});
        this._updateURLFragment({'env_type': new_type});
    }

    async onnewenvdatatimes(new_env_data_times, interval, reset_sel_data_time) {
        let new_state = {'env_data_times': new_env_data_times}

        let prio_env_time = null;
        if (reset_sel_data_time && new_env_data_times.length > 0 && this.state['tor_collection'] !== null) {
            const tc_intvs = interval.getSubintervals(this.env_data_dintv, DOMAINS[this.state['domain']]['cday_offset']);
            const tc_counts = await this.state['tor_collection'].getNTornadoes(tc_intvs.map(sintv => interval.intersection(sintv)));
            const count_max_idx = tc_counts.map(cnt => cnt['count']).indexOf(Math.max(...tc_counts.map(cnt => cnt['count'])));
            prio_env_time = tc_intvs[count_max_idx].start;

            if (new_env_data_times.map(dt => dt.toISO()).includes(prio_env_time.toISO())) {
                new_state['sel_data_time'] = prio_env_time;
            }
            else {
                new_state['sel_data_time'] = null;
            }
        }
        else if (new_env_data_times.length == 0) {
            new_state['sel_data_time'] = null;
        }

        this.setState(new_state);

        if (new_state['sel_data_time'] !== undefined) {
            this._updateURLFragment({'interval': interval, 'sel_data_time': new_state['sel_data_time']});
        }
    }

    onanimtoggle() {
        const turn_on_anim = !this.state['is_animating']
        this.setState({'is_animating': turn_on_anim});
        this.animator.toggleAnimation(turn_on_anim);
    }

    onanimspeedchange(amount) {
        this.animator.anim_delay *= Math.pow(1.5, -amount);
    }

    advanceTime(direction) {
        if (this.state['sel_data_time'] === null) {
            return;
        }

        if (direction === undefined) {
            direction = 1;
        }

        this.animator.advanceTime(direction, this.state['sel_data_time'], this.state['env_data_times']);
    }

    async ondomainchange(new_domain) {
        if (this.base_tor_collection !== null) {
            const new_domain_tor_collection = await this.base_tor_collection.applyFilters({'domain': DOMAINS[new_domain]['filter']});

            if (this.domain_tor_collection !== null) {
                this.domain_tor_collection.deleteInstance();
            }
            this.domain_tor_collection = new_domain_tor_collection;
        }
        this.setState({'domain': new_domain});

        this.env_handler = new EnvironmentDataHandler(ENV_DATA_BASE);
        this.env_handler.getAvailableDates(DOMAINS[new_domain]['env_name'], this.reanalysis_src, 
            () => this.checkEnvDataTimes(this.state['interval'], true));

        this.onfilterchange({});
    }

    onmapmove(new_map_info) {
        this.setState({'map': new_map_info});
        this.stale_url_fragment = true;
    }

    onmapready() {
        if (this.stale_url_fragment) {
            this._updateURLFragment({'map': this.state['map']});
            this.stale_url_fragment = false;
        }
    }

    checkEnvDataTimes(interval, reset_sel_data_time) {
        if (interval.duration() > ENV_INTV_DUR_CUTOFF) {
            this.onnewenvdatatimes([], interval, reset_sel_data_time);
            return;
        }
        const cday_offset = DOMAINS[this.state['domain']]['cday_offset'];
        const dts = interval.getDatetimes(this.env_data_dintv, cday_offset);
        this.onnewenvdatatimes(dts.filter(dt => this.env_handler.hasData(dt, cday_offset)), interval, reset_sel_data_time);
    }

    onshowhidecontrols() {
        this.setState({'controls_visible': !this.state['controls_visible']});
    }

    ontempdisplaychange(display) {
        this.setState({'temp_display': display});
    }

    keypress(event) {
        switch (event.key) {
            case '.':
                this.advanceTime(1);
                break;
            case ',':
                this.advanceTime(-1);
                break;
            case ' ':
                this.onanimtoggle();
                break;
        }
    }
    
    resize() {
        const new_width = window.innerWidth > window.innerHeight ? this.initial_window_height : this.initial_window_width;
        const new_height = window.innerWidth > window.innerHeight ? this.initial_window_width : this.initial_window_height;
        const timeline_xbnds = [TIMELINE_MARGINS['left'], new_width - TIMELINE_MARGINS['right']];

        this.setState({'window_size': {'width': new_width, 'height': new_height}, 
            'timeline_taxis': this.state['timeline_taxis'].withXBounds(timeline_xbnds)});
    }

    render() {
        let map_intv = null; 

        if (this.state['temp_display'] == 'Hourly' 
            || this.state['temp_display'] == 'All' && this.state['is_animating'] && this.state['sel_data_time'] !== null) {
            map_intv = new SelectionInterval(Interval.after(this.state['sel_data_time'], this.env_data_dintv));
        }
        else if (this.state['temp_display'] == 'All') {
            map_intv = this.state['interval'];
        }

        return (
            <div id="main">
                <MapWidget 
                    controls_visible={this.state['controls_visible']}
                    interval={map_intv}
                    tor_coll={this.state['tor_collection']}
                    partitioner={this.partitioner}
                    units={DOMAINS[this.state['domain']]['units']}

                    env_data_times={this.state['env_data_times']}
                    sel_data_time={this.state['sel_data_time']} 
                    env_handler={this.env_handler}
                    env_type={this.state['env_type']}
                    reanalysis_domain={DOMAINS[this.state['domain']]['env_name']}
                    reanalysis_src={this.reanalysis_src}

                    cday_offset={DOMAINS[this.state['domain']]['cday_offset']}
                    fit_bounds={DOMAINS[this.state['domain']]['fit_bounds']}
                    map_position={this.state['map']}

                    setscreenshotfunc={this.set_screenshot_func}

                    onmove={this.onmapmove.bind(this)}
                    onmapready={this.onmapready.bind(this)}
                    onintervalchange={this.onintervalchange.bind(this)}
                />
                <MapOverlayWidget
                    controls_visible={this.state['controls_visible']}
                    interval={this.state['interval']}
                    tor_coll={this.state['tor_collection']}
                    filters={this.state['filters']}
                    data_limits={DATA_LIMITS}
                    sel_data_time={this.state['sel_data_time']}
                    env_handler={this.env_handler}
                    env_type={this.state['env_type']}
                    temp_display={this.state['temp_display']}
                    cday_offset={DOMAINS[this.state['domain']]['cday_offset']}
                    units={DOMAINS[this.state['domain']]['units']}
                    attribution={DOMAINS[this.state['domain']]['attribution']}

                    window_size={this.state['window_size']}

                    setscreenshotfunc={this.set_screenshot_func}
                />
                <ControlWidget 
                    visible={this.state['controls_visible']}
                    interval={this.state['interval']}
                    tor_coll={this.state['tor_collection']}
                    partitioner={this.partitioner}
                    filters={this.state['filters']}
                    timeline_margins={TIMELINE_MARGINS}
                    timeline_taxis={this.state['timeline_taxis']}
                    data_limits={DATA_LIMITS}
                    cday_offset={DOMAINS[this.state['domain']]['cday_offset']}
                    units={DOMAINS[this.state['domain']]['units']}
                    domain_has_states={DOMAINS[this.state['domain']]['has_states']}

                    env_data_times={this.state['env_data_times']}
                    sel_data_time={this.state['sel_data_time']}
                    env_handler={this.env_handler}
                    env_type={this.state['env_type']}
                    is_animating={this.state['is_animating']}
                    temp_display={this.state['temp_display']}
                    env_intv_dur_cutoff={ENV_INTV_DUR_CUTOFF}
                    random_day_func={this.random_day_func}

                    domains={DOMAIN_TITLES}
                    sel_domain={this.state['domain']}

                    window_size={this.state['window_size']}

                    onshowhidecontrols={this.onshowhidecontrols}
                    onintervalchange={this.onintervalchange}
                    onfilterchange={this.onfilterchange}
                    ontboundschange={this.ontboundschange}
                    onenvlayerchange={this.onenvlayerchange}
                    onenvtimechange={this.onenvtimechange}
                    onanimtoggle={this.onanimtoggle}
                    onanimspeedchange={this.onanimspeedchange}
                    onscreenshot={this.onscreenshot}
                    ondomainchange={this.ondomainchange}
                    ontempdisplaychange={this.ontempdisplaychange}

                    setscreenshotfunc={this.set_screenshot_func}
                />
            </div>
        );
    }
}

export default TornadoArchiveApp;
